<template>
  <div ref="containerElement" class="flex flex-auto justify-center overflow-auto" :style="gestureStyling">
    <div
      v-if="!activeGroup?.slides || activeGroup.slides.length === 0"
      class="flex flex-col gap-2 text-center items-center justify-center pa-4"
    >
      <CIcon class="color-primary3" icon="presentation" size="36" />

      <span class="font-size-5 color-primary3">{{ t('canvasUI.collectionPlayer.emptySlide') }}</span>
    </div>

    <swiper-container
      v-else
      ref="swiperElement"
      v-element-visibility="setSwiperVisibility"
      :activeIndex="activeSlideIndex"
      class="w-full h-full"
      :direction="AxisDirection.VERTICAL"
      :loopPreventsSliding="false"
      :pagination="{
        dynamicBullets: true,
        enabled: false,
      }"
      :preventInteractionOnTransition="false"
      :style="{
        '--swiper-pagination-bullet-inactive-color': isFullscreen ? 'white' : 'black',
      }"
      :zoom="{
        containerClass: 'collection-player__zoom-container',
      }"
      @swiperslidechange="onSlideChange"
      @swiperzoomchange="onZoomChangeEvent"
    >
      <swiper-slide
        v-for="(item, index) in activeGroup.slides"
        :key="`${item.id}_${index}`"
        class="flex w-full overflow-auto"
      >
        <SlideContent :item="item" />
      </swiper-slide>
    </swiper-container>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@collection-player/stores/app.store'
import useMovingGroupGestures from '@collection-player/composables/moving-group-gestures.use'
import { AxisDirection } from '@collection-player/composables/slide-direction.use'
import SlideContent from './SlideContent.vue'

const { t } = useI18n()

const {
  swiperElement,
  activeGroup,
  activeSlide,
  zoom: { onZoomChangeEvent },
  fullscreen: { isFullscreen },
  slideDirection: { direction: slideDirection },
  setSwiperVisibility,
  onSlideChange,
  activatePreviousGroup,
  activateNextGroup,
  annotationLayer,
  updateSlideInSwiper,
} = useAppStore()

const containerElement = ref<HTMLElement | null>(null)
const swipeContainerElement = computed(() => (annotationLayer.activeTool.value ? null : containerElement.value))

const updateDirectionInSwiper = (direction: AxisDirection) => {
  if (!swiperElement.value) return

  const paginationElement = swiperElement.value?.swiper?.pagination?.el

  if (paginationElement) {
    paginationElement.removeAttribute('style')

    Array.from(paginationElement.children).forEach((child) => {
      child.removeAttribute('style')
    })
  }

  swiperElement.value.swiper.changeDirection(direction)
  swiperElement.value.swiper.pagination.update()

  // It's internal function, not available in documentation, without that it's not possible to make pagination visible when toggling state
  const pagination = swiperElement.value.swiper.pagination as unknown as { enable: () => void }
  pagination.enable()
}

const { gestureStyling } = useMovingGroupGestures(
  swipeContainerElement,
  {
    onSwipeLeft: () => activatePreviousGroup(),
    onSwipeRight: () => activateNextGroup(),
    onSwipeUp: () => activatePreviousGroup(),
    onSwipeDown: () => activateNextGroup(),
  },
  slideDirection,
)

const activeSlideIndex = computed(() => {
  const index = activeGroup.value?.slides?.findIndex((slide) => slide.id === activeSlide.value?.id) ?? 0

  return index > -1 ? index : 0
})

onMounted(() => {
  updateSlideInSwiper(activeSlideIndex.value)

  watch(
    slideDirection,
    () => {
      if (!slideDirection.value) return

      updateDirectionInSwiper(slideDirection.value)
    },
    { immediate: true },
  )
})
</script>
